import { format } from 'date-fns'
import { Brush, CartesianGrid, Legend, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts'

export interface Series {
  key: string
  name: string
  color: string
}

interface TimeSeriesProps {
  /** Each point has a `time` (ms since epoch) and one numeric value per series key. */
  data: Record<string, number>[]
  series: Series[]
  height?: number
  onRangeChange?: (fromMs: number, toMs: number) => void
}

/** Line chart over time with a brush to zoom; the selected range is reported so tables can follow it. */
export function TimeSeries({ data, series, height = 320, onRangeChange }: TimeSeriesProps) {
  return (
    <div style={{ width: '100%', height }}>
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={data} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
          <CartesianGrid stroke="var(--border)" vertical={false} />
          <XAxis dataKey="time" type="number" domain={['dataMin', 'dataMax']} scale="time" tickFormatter={(v: number) => format(v, 'HH:mm')} tick={{ fontSize: 11 }} />
          <YAxis tick={{ fontSize: 11 }} width={44} />
          <Tooltip
            labelFormatter={(v) => format(Number(v), 'yyyy-MM-dd HH:mm')}
            contentStyle={{ fontSize: 12, background: 'var(--bg-surface)', border: '1px solid var(--border-strong)', borderRadius: 4 }}
          />
          <Legend wrapperStyle={{ fontSize: 12 }} />
          {series.map((s) => (
            <Line key={s.key} type="monotone" dataKey={s.key} name={s.name} stroke={s.color} dot={false} strokeWidth={1.5} isAnimationActive={false} />
          ))}
          <Brush
            dataKey="time"
            height={22}
            stroke="var(--border-strong)"
            tickFormatter={(v: number) => format(v, 'HH:mm')}
            onChange={(range) => {
              if (!onRangeChange || range.startIndex === undefined || range.endIndex === undefined) return
              const from = data[range.startIndex]?.time
              const to = data[range.endIndex]?.time
              if (from !== undefined && to !== undefined) onRangeChange(from, to)
            }}
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  )
}
